 
import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { environment } from '@environments/environment'
import { Area } from '@models/area.model'
import {
  AreaGrowthProfile,
  UpsertAreaGrowthProfileRequest,
} from '@models/areaGrowthProfile.model'
import {
  ClippingRegistration,
  UpsertClippingRegistrationRequest,
} from '@models/clippingRegistration.model'
import {
  DiseaseEvent,
  UpsertDiseaseEventRequest,
} from '@models/diseaseEvent.model'
import { AreaSensor } from '@models/areaSensor.model'
import { Sensor } from '@models/sensor.model'
import { Threshold } from '@models/threshold.model'
import { normalizeGeofence } from '@utils/geofence'
import { map } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class AreasService {
  private readonly http = inject(HttpClient)

  getAreasForCompanies(companyIds: number[]) {
    return this.http
      .get<Area[]>(`${environment.apiEndPoint}/Areas/withDevicesForCompanies`, {
        params: {
          companyIds: companyIds.join(','),
        },
      })
      .pipe(
        map((areas) =>
          areas.map((area) => {
            area.geofence = normalizeGeofence(area.geofence)
            area.company = { ...area.company, id: area.companyId }
            return area
          }),
        ),
      )
  }

  getMyAreas() {
    return this.http
      .get<Area[]>(environment.apiEndPoint + '/Areas/myAreas')
      .pipe(
        map((areas) =>
          areas.map((area) => {
            area.geofence = normalizeGeofence(area.geofence)
            return area
          }),
        ),
      )
  }

  getAreaById(areaId: number) {
    return this.http
      .get<Area>(environment.apiEndPoint + `/Areas/${areaId}`)
      .pipe(
        map((area) => {
          area.geofence = normalizeGeofence(area.geofence)
          return area
        }),
      )
  }

  getSensorsFromAreaById(id: number) {
    return this.http.get<AreaSensor[]>(
      environment.apiEndPoint + `/Areas/${id}/devices`,
    )
  }
  getAvailableSensors(areaId: number) {
    return this.http.get<Sensor[]>(
      environment.apiEndPoint + `/Areas/${areaId}/devices/available`,
    )
  }
  addSensorToArea(
    areaId: number,
    sensor: AreaSensor,
    thresholds: Record<string, number>,
    alerts: Record<string, boolean>,
    startDate: Date,
    endDate?: Date,
  ): import('rxjs').Observable<AreaSensor> {
    return this.http.post<AreaSensor>(
      environment.apiEndPoint + `/Areas/${areaId}/devices`,
      {
        devEUI: sensor.id,
        name: sensor.name,
        location: sensor.location,
        startDate,
        endDate,
        thresholds,
        alerts,
      },
    )
  }

  getThreshold(id: number) {
    return this.http.get<Threshold>(
      environment.apiEndPoint + `/Areas/${id}/threshold`,
    )
  }
  saveThreshold(areaId: number, threshold: Threshold) {
    return this.http.patch<Threshold>(
      environment.apiEndPoint + `/Areas/${areaId}/threshold`,
      threshold,
    )
  }

  updateSensorLocation(
    areaId: number,
    sensorId: number,
    location: { lat: number; lng: number },
  ) {
    return this.http.patch(
      environment.apiEndPoint + `/Areas/${areaId}/devices/${sensorId}/location`,
      { x: location.lng, y: location.lat },
    )
  }

  updateSensorName(areaId: number, id: number, name: string) {
    return this.http.patch(
      `${environment.apiEndPoint}/Areas/${areaId}/devices/${id}/name`,
      { name },
    )
  }

  replaceSensor(areaId: number, sensorId: number, replacementDevEUI: string) {
    return this.http.patch<AreaSensor>(
      `${environment.apiEndPoint}/Areas/${areaId}/devices/${sensorId}/replace`,
      { devEUI: replacementDevEUI },
    )
  }

  removeSensor(areaId: number, sensorId: number) {
    return this.http.delete(
      `${environment.apiEndPoint}/Areas/${areaId}/devices/${sensorId}`,
    )
  }

  sendDownlink(areaId: number, sensorId: number, bytes: string) {
    return this.http.post(
      `${environment.apiEndPoint}/Areas/${areaId}/devices/${sensorId}/downlink`,
      { bytes },
    )
  }

  /**
   * Update an entire area object. Only coordinates/name/threshold are currently supported.
   * This method is used when editing the polygon or other area properties.
   */
  updateArea(
    areaId: number,
    areaName: string,
    areaType: string,
    coordinates: number[][][][],
    threshold: Threshold,
  ) {
    return this.http
      .put<Area>(
        `${environment.apiEndPoint}/Areas/${areaId}`,
        {
          areaName,
          areaType,
          coordinates,
          threshold,
        },
      )
      .pipe(
        map((area) => {
          area.geofence = normalizeGeofence(area.geofence)
          return area
        }),
      )
  }

  updateSurfaceM2(areaId: number, surfaceM2: number) {
    return this.http
      .patch<Area>(`${environment.apiEndPoint}/Areas/${areaId}/surface`, {
        surfaceM2: Math.round(surfaceM2),
      })
      .pipe(
        map((area) => {
          area.geofence = normalizeGeofence(area.geofence)
          return area
        }),
      )
  }

  deleteArea(areaId: number) {
    return this.http.delete(`${environment.apiEndPoint}/Areas/${areaId}`)
  }

  getAreaGrowthProfile(areaId: number) {
    return this.http.get<AreaGrowthProfile>(
      `${environment.apiEndPoint}/Areas/${areaId}/growth-profile`,
    )
  }

  upsertAreaGrowthProfile(
    areaId: number,
    payload: UpsertAreaGrowthProfileRequest,
  ) {
    return this.http.put<AreaGrowthProfile>(
      `${environment.apiEndPoint}/Areas/${areaId}/growth-profile`,
      payload,
    )
  }

  deleteAreaGrowthProfile(areaId: number) {
    return this.http.delete(`${environment.apiEndPoint}/Areas/${areaId}/growth-profile`)
  }

  getAreaClippings(areaId: number, fromUtc?: string, toUtc?: string) {
    const params: Record<string, string> = {}
    if (fromUtc) {
      params['fromUtc'] = fromUtc
    }
    if (toUtc) {
      params['toUtc'] = toUtc
    }

    return this.http.get<ClippingRegistration[]>(
      `${environment.apiEndPoint}/Areas/${areaId}/clippings`,
      { params },
    )
  }

  createAreaClipping(
    areaId: number,
    payload: UpsertClippingRegistrationRequest,
  ) {
    return this.http.post<ClippingRegistration>(
      `${environment.apiEndPoint}/Areas/${areaId}/clippings`,
      payload,
    )
  }

  updateAreaClipping(
    areaId: number,
    clippingId: number,
    payload: UpsertClippingRegistrationRequest,
  ) {
    return this.http.put<ClippingRegistration>(
      `${environment.apiEndPoint}/Areas/${areaId}/clippings/${clippingId}`,
      payload,
    )
  }

  deleteAreaClipping(areaId: number, clippingId: number) {
    return this.http.delete(
      `${environment.apiEndPoint}/Areas/${areaId}/clippings/${clippingId}`,
    )
  }

  getAreaDiseaseEvents(areaId: number, fromUtc?: string, toUtc?: string) {
    const params: Record<string, string> = {}
    if (fromUtc) {
      params['fromUtc'] = fromUtc
    }
    if (toUtc) {
      params['toUtc'] = toUtc
    }

    return this.http.get<DiseaseEvent[]>(
      `${environment.apiEndPoint}/Areas/${areaId}/disease-events`,
      { params },
    )
  }

  createAreaDiseaseEvent(
    areaId: number,
    payload: UpsertDiseaseEventRequest,
  ) {
    return this.http.post<DiseaseEvent>(
      `${environment.apiEndPoint}/Areas/${areaId}/disease-events`,
      payload,
    )
  }

  updateAreaDiseaseEvent(
    areaId: number,
    diseaseEventId: number,
    payload: UpsertDiseaseEventRequest,
  ) {
    return this.http.put<DiseaseEvent>(
      `${environment.apiEndPoint}/Areas/${areaId}/disease-events/${diseaseEventId}`,
      payload,
    )
  }

  deleteAreaDiseaseEvent(areaId: number, diseaseEventId: number) {
    return this.http.delete(
      `${environment.apiEndPoint}/Areas/${areaId}/disease-events/${diseaseEventId}`,
    )
  }
}
